/**
 * Renders a country flag emoji from a 2-letter ISO 3166-1 alpha-2 code.
 *
 * Flags are produced by mapping each ASCII letter to its
 * regional-indicator counterpart (e.g. "nl" → 🇳🇱). This avoids shipping
 * any flag image assets and works on every platform that ships the
 * regional-indicator block (macOS, iOS, modern Android, Windows 11+).
 *
 * Falls back to the `Globe2` lucide icon when the ISO code is missing
 * or invalid so customers without a configured country still see a
 * sensible glyph instead of a broken layout.
 */
import { Globe2 } from "lucide-react";

export interface CountryFlagProps {
  /** ISO 3166-1 alpha-2 (case insensitive). Empty/invalid renders the fallback. */
  iso2?: string | null;
  /** Tooltip / a11y label, typically the country's full name. */
  title?: string;
  /** Pixel size for the fallback icon and inline emoji wrapper. */
  size?: number;
}

export function CountryFlag({ iso2, title, size = 18 }: CountryFlagProps) {
  const code = (iso2 ?? "").trim().toLowerCase();
  const valid = code.length === 2 && /^[a-z]{2}$/.test(code);

  if (!valid) {
    return (
      <Globe2
        size={size}
        aria-label={title ?? "Unknown country"}
        style={{ color: "var(--portal-muted)" }}
      />
    );
  }

  // Regional-indicator base codepoint is 0x1F1E6 ("A"); offset every
  // letter by its position in the alphabet to land on the right pair.
  const A = "a".charCodeAt(0);
  const base = 0x1f1e6;
  const flag = String.fromCodePoint(
    base + (code.charCodeAt(0) - A),
    base + (code.charCodeAt(1) - A),
  );

  return (
    <span
      role="img"
      aria-label={title ?? code.toUpperCase()}
      title={title ?? code.toUpperCase()}
      style={{
        display: "inline-block",
        fontSize: size,
        lineHeight: `${size}px`,
        // Twemoji-style fonts render flags wider than their reported
        // width; clamp the box so the surrounding flex layout doesn't
        // jump when a row has no flag at all.
        width: `${Math.round(size * 1.4)}px`,
        textAlign: "center",
      }}
    >
      {flag}
    </span>
  );
}
